import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { AgreementProductItem, OrderCustomerItem, ProductItem, VatItem } from 'src/app/api/models';
import { ModalAgreementProdPickerComponent } from '../modal-agreement-prod-picker/modal-agreement-prod-picker.component';
import { VatService } from 'src/app/api/services';
import { environment } from 'src/environments/environment';

type OrderItem = {
  id: number;
  product_id?: number;
  name?: string;
  code_internal?: string;
  qty?: number;
  agreement_code?: string;
  [key: string]: any;
};

@Component({
  selector: 'app-modal-replace-order-product',
  templateUrl: './modal-replace-order-product.component.html',
  styleUrls: ['./modal-replace-order-product.component.scss'],
})
export class ModalReplaceOrderProductComponent implements OnInit {
  order: OrderCustomerItem;
  items: OrderItem[] = [];

  // Stepper: 1 = select old, 2 = select new + impact
  step = 1;

  selectedOldItem: OrderItem | null = null;
  selectedNewAgreementProduct: AgreementProductItem | null = null;
  selectedNewProduct: ProductItem | null = null;

  impact: {
    affected_deliveries: number;
    affected_tickets: number;
    blocking_ddt_deliveries: number;
    assetized_qty: number;
    replaceable_qty: number;
    defaults?: {
      qty: number | null;
      order_price: number | null;
      vat_id: number | null;
      vat_label: string | null;
      vat_factor: number | null;
    };
  } | null = null;
  loadingImpact = false;
  executing = false;

  // User-editable replacement values
  newPrice: number | null = null;
  selectedVat: VatItem | null = null;
  vatList: VatItem[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private http: HttpClient,
    private vatService: VatService
  ) {
    this.order = config.data.order as OrderCustomerItem;
    this.items = ((this.order.items as any[]) ?? []).map(i => i as OrderItem);
  }

  ngOnInit() {
    this.vatService.vatControllerGetAll().subscribe({
      next: res => {
        this.vatList = res.data?.list ?? [];
      },
    });
  }

  /** true = mostra SOLO il picker della commessa; false = mostra entrambi i picker */
  get onlyAgreementProducts(): boolean {
    return this.order.related_agreement?.only_agreement_products ?? false;
  }

  get newProductLabel(): string {
    if (this.selectedNewAgreementProduct) {
      return `${this.selectedNewAgreementProduct.name || ''} (${this.selectedNewAgreementProduct.code_internal || ''})`;
    }
    if (this.selectedNewProduct) {
      return `${(this.selectedNewProduct as any).name || ''} (${(this.selectedNewProduct as any).code_internal || ''})`;
    }
    return '—';
  }

  get hasNewProduct(): boolean {
    return !!(this.selectedNewAgreementProduct || this.selectedNewProduct);
  }

  selectOldItem(item: OrderItem) {
    this.selectedOldItem = item;
    this.selectedNewAgreementProduct = null;
    this.selectedNewProduct = null;
    this.impact = null;
  }

  goToStep2() {
    if (!this.selectedOldItem) return;
    this.step = 2;
  }

  openAgreementProductPicker() {
    const oldAgreementProductId = this.selectedOldItem?.product_id ? this.selectedOldItem.id : null;
    const oldStagingActivities = this.selectedOldItem?.['staging_activities'] ?? null;
    const oldMinQtyTesting = this.selectedOldItem?.['min_qty_testing'] ?? null;

    // Calcola motivi di incompatibilità per ogni prodotto della commessa
    const disabledReasons: Record<number, string> = {};
    for (const p of (this.order.related_agreement?.items ?? []) as any[]) {
      if (oldAgreementProductId && p.id === oldAgreementProductId) continue;
      const reasons: string[] = [];
      if (p.staging_activities !== oldStagingActivities)
        reasons.push('attività di staging diverse');
      if (p.min_qty_testing !== oldMinQtyTesting) reasons.push('requisiti di collaudo diversi');
      if (reasons.length > 0) disabledReasons[p.id] = `Non selezionabile: ${reasons.join(', ')}`;
    }

    const pickerRef = this.dialogService.open(ModalAgreementProdPickerComponent, {
      header: 'Seleziona nuovo prodotto commessa',
      width: '80vw',
      data: {
        question_title: 'Seleziona il nuovo prodotto',
        products: this.order.related_agreement?.items ?? [],
        pickerType: 'radio',
        chosenProducts: oldAgreementProductId ? [{ id: oldAgreementProductId }] : [],
        disabledReasons,
      },
    });
    pickerRef.onClose.subscribe((res: any) => {
      if (res?.value) {
        this.selectedNewAgreementProduct = res.value as AgreementProductItem;
        this.selectedNewProduct = null;
        this.impact = null;
        this.newPrice = null;
        this.selectedVat = null;
        this.fetchImpact();
      }
    });
  }

  fetchImpact() {
    if (!this.selectedOldItem) return;
    this.loadingImpact = true;
    this.impact = null;

    const oldAgreementProductId = this.selectedOldItem.product_id
      ? this.selectedOldItem.id
      : undefined;
    const oldProductId = this.selectedOldItem.product_id
      ? this.selectedOldItem.product_id
      : this.selectedOldItem.id;

    let params: any = {};
    if (oldAgreementProductId) {
      params['old_agreement_product_id'] = oldAgreementProductId;
    } else {
      params['old_product_id'] = oldProductId;
    }

    // Include new product params to get defaults
    if (this.selectedNewAgreementProduct) {
      params['new_agreement_product_id'] = this.selectedNewAgreementProduct.id;
    } else if (this.selectedNewProduct) {
      params['new_product_id'] = (this.selectedNewProduct as any).id;
    }

    this.http
      .get<any>(`${environment.baseUrl}/order-customer/${this.order.id}/replace-product/impact`, {
        params,
      })
      .subscribe({
        next: res => {
          this.impact = res.data;
          this.loadingImpact = false;
          // Pre-fill editable fields from defaults
          const d = res.data?.defaults;
          if (d) {
            this.newPrice = d.order_price ?? null;
            if (d.vat_id && this.vatList.length > 0) {
              this.selectedVat = this.vatList.find(v => v.id === d.vat_id) ?? null;
            } else if (d.vat_id) {
              // vatList not loaded yet — create a minimal object
              this.selectedVat = {
                id: d.vat_id,
                label: d.vat_label ?? '',
                factor: d.vat_factor ?? 1,
              };
            }
          }
        },
        error: err => {
          this.loadingImpact = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message ?? 'Errore nel calcolo impatto',
          });
        },
      });
  }

  goBack() {
    this.step = 1;
    this.selectedNewAgreementProduct = null;
    this.selectedNewProduct = null;
    this.impact = null;
    this.newPrice = null;
    this.selectedVat = null;
  }

  confirmReplace() {
    const oldName = this.selectedOldItem?.name || `#${this.selectedOldItem?.id}`;
    const newName =
      this.selectedNewAgreementProduct?.name ?? (this.selectedNewProduct as any)?.name ?? '?';

    // Prima conferma
    this.confirmationService.confirm({
      message: `Stai per sostituire il prodotto <strong>${oldName}</strong> con <strong>${newName}</strong> nell'ordine e in ${this.impact?.affected_deliveries ?? 0} consegne e ${this.impact?.affected_tickets ?? 0} ticket. Continuare?`,
      header: 'Conferma sostituzione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, continua',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        // Seconda conferma — setTimeout necessario: PrimeNG non supporta confirm annidati senza delay
        setTimeout(
          () =>
            this.confirmationService.confirm({
              message: `⚠️ <strong>ATTENZIONE</strong>: questa operazione è <strong>irreversibile</strong>.<br><br>Una volta confermata, il prodotto verrà aggiornato in tutte le entità non definitive. Sei assolutamente sicuro?`,
              header: 'Conferma definitiva',
              icon: 'pi pi-exclamation-triangle',
              acceptLabel: 'Sì, sono sicuro',
              rejectLabel: 'Annulla',
              acceptButtonStyleClass: 'p-button-danger',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this.executeReplace();
              },
            }),
          300
        );
      },
    });
  }

  private executeReplace() {
    this.executing = true;

    const oldAgreementProductId = this.selectedOldItem!.product_id
      ? this.selectedOldItem!.id
      : undefined;
    const oldProductId = this.selectedOldItem!.product_id
      ? this.selectedOldItem!.product_id
      : this.selectedOldItem!.id;

    const body: any = {};
    if (oldAgreementProductId) {
      body['old_agreement_product_id'] = oldAgreementProductId;
    } else {
      body['old_product_id'] = oldProductId;
    }

    if (this.selectedNewAgreementProduct) {
      body['new_agreement_product_id'] = this.selectedNewAgreementProduct.id;
      body['new_product_id'] = this.selectedNewAgreementProduct.product_id;
    } else if (this.selectedNewProduct) {
      body['new_product_id'] = (this.selectedNewProduct as any).id;
    }

    if (this.newPrice !== null && this.newPrice !== undefined) body['order_price'] = this.newPrice;
    if (this.selectedVat) body['vat_id'] = this.selectedVat.id;

    this.http
      .post<any>(`${environment.baseUrl}/order-customer/${this.order.id}/replace-product`, body)
      .subscribe({
        next: () => {
          this.executing = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Sostituzione effettuata',
            detail: 'Il prodotto è stato sostituito con successo.',
          });
          this.ref.close(true);
        },
        error: err => {
          this.executing = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message ?? 'Errore durante la sostituzione',
          });
        },
      });
  }

  close() {
    this.ref.close();
  }
}
